import { Component, DestroyRef, effect, inject, OnInit, signal, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { LoadingService } from '../../../shared/services/loading.service';
import { ProcessHelperService } from '../../services/process-helper.service';
import { ProcessService } from '../../services/process.service';
import {
	BOOLEAN_STATUS,
	IProcessDetailResponse,
	IProcessFieldHistoryItem,
	OCR_CAPTURE_METHOD_MIDNI,
	PROCESS_STATUS,
	TAB_PROCESS,
} from '../../models/processes.models';
import { collectOcrAdminImageUrls } from '../../utils/ocr-admin-images.util';
import { HttpErrorResponse } from '@angular/common/http';
import { catchError, finalize, of } from 'rxjs';
import {
	buildPersonUpdatePayloadFromOcr,
	ocrDiffersFromIdentity,
} from '../../../identities/utils/person-edit.util';
import { ModalConfirmationService } from '../../../shared/services/modal-confirmation.service';
import { ModalEditIdentityComponent } from '../../../shared/components/modal-edit-identity/modal-edit-identity.component';
import {
	AsideLayoutComponent,
	ButtonIconComponent,
	MenuComponent,
	TileComponent,
	TagComponent,
	IconComponent,
	InfoGroupComponent,
	TabsComponent,
	ITab,
	AlertComponent,
	EmptyStateComponent,
	DividerComponent,
	Icons,
	ITagIconStatus,
	ItemComponent,
	ITagIcon,
	LinkComponent,
	StatsComponent,
	IMenu,
	ModalComponent,
	TimelineComponent,
	ITagStats,
	IBackEvent,
	BadgeComponent,
	CommentsComponent,
	ItemGroupComponent,
} from '@tecalisdev/infinity';
import { MasterService } from '../../../shared/services/master.service';
import { CommonModule } from '@angular/common';
import {
	EVALUATION_TYPE,
	OPERATION_STATUS,
	OPERATION_TYPE,
} from '../../../operations/models/operations.models';
import { BreakpointObserver } from '@angular/cdk/layout';
import { downloadJsonAsTxt, tablet, notAvailableLabel } from '../../../core/utils/utils';
import { SafeDatePipe } from '../../../core/pipes/safe-date.pipe';
import { MenuItemAction } from '../../../shared/models/master.models';
import { ModalChangeStatusComponent } from '../../../shared/components/modal-change-status/modal-change-status.component';
import { ModalJsonViewerComponent } from '../../../shared/components/modal-json-viewer/modal-json-viewer.component';
import { applyProcessDeviceUserAgentParsing } from '../../../core/utils/enriched-user-agent';
import { ProcessDevicesTileDirective, ProcessDeviceBlockUnlockEvent } from '../../components/process-devices-tile/process-devices-tile.directive';
import { MapsLocalizationComponent } from '../../../shared/components/maps-localization/maps-localization.component';
import { ViewMediaService } from '../../../shared/services/view-media.service';
import { OperationDetailDocumentsComponent } from '../../components/operations/operation-detail-documents/operation-detail-documents.component';
import { OperationDetailFormComponent } from '../../components/operations/operation-detail-form/operation-detail-form.component';
import { OperationDetailFinancialEvaluationComponent } from '../../components/operations/operation-detail-financial-evaluation/operation-detail-financial-evaluation.component';
import { OperationDetailOcrComponent } from '../../components/operations/operation-detail-ocr/operation-detail-ocr.component';
import { OperationDetailPolComponent } from '../../components/operations/operation-detail-pol/operation-detail-pol.component';
import { OperationDetailTfaComponent } from '../../components/operations/operation-detail-tfa/operation-detail-tfa.component';
import { ProcessDetailAsideComponent } from '../../components/process-detail-aside/process-detail-aside.component';
import { ModalBlockIdentityComponent } from '../../../shared/components/modal-block-identity/modal-block-identity.component';
import { ModalUnlockIdentityComponent } from '../../../shared/components/modal-unlock-identity/modal-unlock-identity.component';
import { IIdentityBlockPayload, IIdentityDetailBody } from '../../../identities/models/identities.models';
import { IdentitiesService } from '../../../identities/services/identities.service';
import { RolePermissionsService } from '../../../core/services/role-permissions.service';
import { UuidDisplayComponent } from '../../../shared/components/uuid-display/uuid-display.component';
import { NavigationService } from '../../../shared/services/navigation.service';
import { UserActivityTrackerService } from '../../../shared/services/user-activity-tracker.service';
import { LanguageService } from '../../../core/services/language.service';
import {
	normalizePolicyModalInfo,
	PolicyModalInfo,
	policyOriginLabel,
	resolvePolicyOrigin,
} from '../../../core/utils/policy-check.utils';
import { DetailResultTileComponent } from '../../components/detail-result-tile/detail-result-tile.component';
import { ProcessChecksPanelsComponent } from '../../components/process-checks-panels/process-checks-panels.component';
import { ModalIdentityProcessDetailComponent } from '../../components/modal-identity-process-detail/modal-identity-process-detail.component';
import {
	ModalUserAgentComponent,
	DeviceUserAgentInput,
} from '../../components/modal-user-agent/modal-user-agent.component';
import { ModalSentMessagesComponent } from '../../components/modal-sent-messages/modal-sent-messages.component';
import { mergeSentMessages } from '../../utils/sent-messages.util';
import { environment } from '../../../../environments/environment';
import { ListOperationsActivityComponent } from '../../../shared/components/operations/common/list-operations-activity/list-operations-activity.component';
import { ProcessDetailMediaPreviewsComponent } from '../../components/process-detail-media-previews/process-detail-media-previews.component';
import { DetailResourceErrorComponent } from '../../../shared/components/detail-resource-error/detail-resource-error.component';
import { DetailResourceErrorView, resolveDetailResourceError } from '../../../core/utils/http-resource-error.utils';
import { Permission } from '../../../core/models/role-permissions.models';
import { DeveloperCurlButtonComponent } from '../../../shared/components/developer-curl-button/developer-curl-button.component';

@Component({
	selector: 'app-process-detail',
	imports: [
		CommonModule,
		SafeDatePipe,
		TranslatePipe,
		AsideLayoutComponent,
		ButtonIconComponent,
		MenuComponent,
		TileComponent,
		TagComponent,
		IconComponent,
		InfoGroupComponent,
		TabsComponent,
		AlertComponent,
		EmptyStateComponent,
		DividerComponent,
		ItemComponent,
		LinkComponent,
		StatsComponent,
		ModalChangeStatusComponent,
		ModalJsonViewerComponent,
		ProcessDevicesTileDirective,
		MapsLocalizationComponent,
		CommentsComponent,
		ItemGroupComponent,
		OperationDetailDocumentsComponent,
		OperationDetailFormComponent,
		OperationDetailFinancialEvaluationComponent,
		OperationDetailOcrComponent,
		OperationDetailPolComponent,
		OperationDetailTfaComponent,
		ProcessDetailAsideComponent,
		ModalComponent,
		ModalBlockIdentityComponent,
		ModalUnlockIdentityComponent,
		TimelineComponent,
		UuidDisplayComponent,
		ListOperationsActivityComponent,
		DetailResultTileComponent,
		ProcessChecksPanelsComponent,
		ProcessDetailMediaPreviewsComponent,
		BadgeComponent,
		ModalIdentityProcessDetailComponent,
		ModalUserAgentComponent,
		DetailResourceErrorComponent,
		ModalSentMessagesComponent,
		DeveloperCurlButtonComponent,
		ModalEditIdentityComponent,
	],
	templateUrl: './process-detail.component.html',
	styleUrl: './process-detail.component.scss',
})
export class ProcessDetailComponent implements OnInit {
	activatedRoute = inject(ActivatedRoute);
	loading = inject(LoadingService);
	public processHelper = inject(ProcessHelperService);
	processService = inject(ProcessService);
	masterService = inject(MasterService);
	trans = inject(TranslateService);
	breakpointObserver = inject(BreakpointObserver);
	viewMedia = inject(ViewMediaService);
	private router = inject(Router);
	private navigationService = inject(NavigationService);
	private identitiesService = inject(IdentitiesService);
	private userActivityTracker = inject(UserActivityTrackerService);
	rolePermissions = inject(RolePermissionsService);
	private destroyRef = inject(DestroyRef);
	private languageService = inject(LanguageService);
	private modalConfirmationService = inject(ModalConfirmationService);

	public detail: IProcessDetailResponse;
	public id: string;
	public tabs: ITab[] = this.processHelper.tabs;
	public error: HttpErrorResponse | null = null;
	public resourceError: DetailResourceErrorView | null = null;
	public errorActionMsg: string = '';
	public successActionMsg: string = '';
	/** NIF-1047: sync identidad ← OCR en curso. */
	public updateIdentityFromOcrLoading = false;
	public showModalEditIdentity = false;
	public OPERATION_STATUS = OPERATION_STATUS;
	public OPERATION_TYPE = OPERATION_TYPE;
	public PROCESS_STATUS = PROCESS_STATUS;
	public BOOLEAN_STATUS = BOOLEAN_STATUS;
	public EVALUATION_TYPE = EVALUATION_TYPE;
	public tabletSize: boolean = false;
	public showModalChangeStatus: boolean = false;
	public showModalBlockIdentity: boolean = false;
	public selectedOptionsIdentity?: string[];
	/** IP concreta del candado en tile Dispositivos; null = menú genérico (todas las IPs del proceso). */
	public selectedDeviceIp: string | null = null;
	public showModalUnlockIdentity: boolean = false;
	public showModalJsonViewer: boolean = false;
	public showModalIdentityDetail: boolean = false;
	public policyModalInfo: (PolicyModalInfo & { html: string }) | null = null;
	public activityJsonModal: { title: string; data: any } | null = null;
	public deviceForUserAgentModal: DeviceUserAgentInput | null = null;
	public showSentMessagesModal = signal(false);
	public menuItemAction = MenuItemAction;
	public TAB_PROCESS = TAB_PROCESS;
	/** Descarga de PDF de auditoría en curso (`none` si no hay ninguna). */
	public readonly downloadActive = signal<'none' | 'summary' | 'signed'>('none');
	public readonly tabLoading = signal<TAB_PROCESS | null>(null);
	public readonly operationLoading = signal<Set<OPERATION_TYPE>>(new Set());
	public readonly loadedSections = signal<Set<TAB_PROCESS>>(new Set());
	public readonly loadedOperations = signal<Set<OPERATION_TYPE>>(new Set());
	public readonly mediaSupplementsLoading = signal(false);
	public readonly previewImagesLoading = signal(false);
	/** NIF-741 — mapa `block|field` → cambio más reciente. */
	public readonly fieldHistoryByKey = signal<Map<string, IProcessFieldHistoryItem>>(new Map());
	/** Operaciones con payload full ya consumidas por Comprobaciones. */
	private readonly checksOperationsHydrated = signal<Set<OPERATION_TYPE>>(new Set());
	/** Operaciones con payload full ya consumidas por Actividad (`HOME` solo vía `view=operation`). */
	private readonly activityOperationsHydrated = signal<Set<OPERATION_TYPE>>(new Set());
	public expandedOperation: OPERATION_TYPE | null = null;
	private pendingOperationKeys: OPERATION_TYPE[] = [];
	private operationsFetchInFlight = false;

	/** DOCUMENTS / EVALUATION / ATTORNEY_POWERS en el mismo orden que el timeline (`Object.keys`). */
	public get documentsEvaluationCollapseKeys(): OPERATION_TYPE[] {
		const allowed = new Set<string>([
			OPERATION_TYPE.DOCUMENTS,
			OPERATION_TYPE.EVALUATION,
			OPERATION_TYPE.ATTORNEY_POWERS,
		]);
		const data = this.detail?.data;
		if (!data) {
			return [];
		}
		return Object.keys(data).filter(
			(key): key is OPERATION_TYPE => allowed.has(key) && !!(data as Record<string, unknown>)[key],
		);
	}

	@ViewChild('menuCurlBtn') private menuCurlBtn?: DeveloperCurlButtonComponent;

	constructor() {
		effect(() => {
			const modalResponse = this.modalConfirmationService.closeModal();
			if (!modalResponse) {
				return;
			}
			if (modalResponse.id === 'sync-identity-from-ocr-confirm') {
				if (modalResponse.action === 'buttonPrimary') {
					this.syncIdentityFromOcr();
				}
				return;
			}
			if (modalResponse.id === 'sync-identity-reassign') {
				if (modalResponse.action === 'buttonPrimary') {
					this.syncIdentityFromOcr({ overwriteExisting: true });
				}
				return;
			}
			if (modalResponse.id === 'sync-identity-underage') {
				if (modalResponse.action === 'buttonPrimary') {
					this.syncIdentityFromOcr({ confirmUnderageLegalAge: true });
				}
			}
		});
	}

	ngOnInit(): void {
		this.breakpointObserver.observe([tablet]).subscribe((x) => {
			this.tabletSize = x.breakpoints[tablet];
		});
		this.id = this.activatedRoute.snapshot.params['id'];
		this.getDetail();

		// Actualizar timeline y actividad al cambiar de idioma (títulos, tooltips, estados)
		this.trans.onLangChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
			this.tabs = this.processHelper.tabs;
			if (this.detail) {
				this.processHelper.setTimeline(this.detail);
				if (this.loadedSections().has(TAB_PROCESS.ACTIVITY)) {
					this.processHelper.setActivityLogs(this.detail);
				}
			}
		});
	}

	/** NIF-1047: CTA visible si OCR difiere de la identidad y hay permiso de edición. */
	public get showUpdateIdentityFromOcr(): boolean {
		return (
			this.rolePermissions.canEditPerson() &&
			!!this.detail?.identity?.uuid &&
			!!this.detail?.data?.OCR?.uuid &&
			ocrDiffersFromIdentity(this.detail.identity, this.detail.data.OCR)
		);
	}

	/** NIF-1047: lápiz de edición (aside + OCR) si hay Person y permiso. */
	public get showEditIdentity(): boolean {
		return this.rolePermissions.canEditPerson() && !!this.detail?.identity?.uuid;
	}

	/** Detalle Person mínimo para reutilizar `app-modal-edit-identity`. */
	public get identityDetailForEdit(): IIdentityDetailBody | null {
		const identity = this.detail?.identity;
		if (!identity?.uuid) {
			return null;
		}
		return {
			uuid: identity.uuid,
			names: identity.names,
			surnames: identity.surnames,
			identifier: identity.identifier,
			document_type: identity.document_type,
			expiration_date: identity.expiration_date,
			birth_date: identity.birthdate,
			sex: identity.gender,
			nationality: identity.nationality,
			primary_process_uuid: this.id,
			company_uuid: this.detail?.general?.company_uuid,
		};
	}

	public openEditIdentity(): void {
		if (!this.identityDetailForEdit) {
			return;
		}
		this.showModalEditIdentity = true;
	}

	public closeEditIdentity(saved: boolean): void {
		this.showModalEditIdentity = false;
		if (saved) {
			this.successActionMsg = this.trans.instant('PROCESSES.DETAIL.UPDATE_IDENTITY_FROM_OCR_SUCCESS');
			this.getDetail();
		}
	}

	/** NIF-1047: modal de advertencia antes de sincronizar identidad ← OCR. */
	public confirmSyncIdentityFromOcr(): void {
		if (!this.showUpdateIdentityFromOcr || this.updateIdentityFromOcrLoading) {
			return;
		}
		this.modalConfirmationService.show({
			id: 'sync-identity-from-ocr-confirm',
			title: this.trans.instant('PROCESSES.DETAIL.UPDATE_IDENTITY_FROM_OCR_CONFIRM_TITLE'),
			description: this.trans.instant('PROCESSES.DETAIL.UPDATE_IDENTITY_FROM_OCR_CONFIRM_DESCRIPTION'),
			buttonPrimaryLabel: this.trans.instant('BUTTON.UPDATE'),
			buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
			buttonPrimaryStyle: true,
		});
	}

	/**
	 * Actualiza la Person con los datos OCR del proceso actual.
	 * Maneja 409 IDENTIFIER_EXISTS / LEGAL_AGE_BELOW_MINIMUM como en review.
	 */
	public syncIdentityFromOcr(
		options: { overwriteExisting?: boolean; confirmUnderageLegalAge?: boolean } = {},
	): void {
		const identityUuid = this.detail?.identity?.uuid;
		const ocr = this.detail?.data?.OCR;
		if (!identityUuid || !ocr?.uuid || !this.id) {
			return;
		}
		this.updateIdentityFromOcrLoading = true;
		this.errorActionMsg = '';
		this.successActionMsg = '';
		const body = buildPersonUpdatePayloadFromOcr(this.id, ocr, options);
		this.processService.updatePersonOperation(identityUuid, body).subscribe({
			next: () => {
				this.updateIdentityFromOcrLoading = false;
				this.successActionMsg = this.trans.instant('PROCESSES.DETAIL.UPDATE_IDENTITY_FROM_OCR_SUCCESS');
				this.getDetail();
			},
			error: (error: HttpErrorResponse) => {
				this.updateIdentityFromOcrLoading = false;
				if (error.status === 409 && error.error?.code === 'IDENTIFIER_EXISTS') {
					this.modalConfirmationService.show({
						id: 'sync-identity-reassign',
						title: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_DESCRIPTION'),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.REASSIGN_IDENTITY_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				if (error.status === 409 && error.error?.code === 'LEGAL_AGE_BELOW_MINIMUM') {
					const yearsOld = Number(error.error?.data?.years);
					const legalAge = Number(error.error?.data?.legal_age);
					this.modalConfirmationService.show({
						id: 'sync-identity-underage',
						title: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_TITLE'),
						description: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_DESCRIPTION', {
							yearsOld: Number.isFinite(yearsOld) ? yearsOld : 0,
							legalAge: Number.isFinite(legalAge) ? legalAge : 0,
						}),
						buttonPrimaryLabel: this.trans.instant('PROCESSES.REVIEW.UNDERAGE_LEGAL_AGE_ACCEPT'),
						buttonSecondaryLabel: this.trans.instant('BUTTON.CANCEL'),
						buttonPrimaryStyle: true,
					});
					return;
				}
				this.errorActionMsg =
					error?.error?.message ||
					this.trans.instant('PROCESSES.DETAIL.UPDATE_IDENTITY_FROM_OCR_ERROR');
			},
		});
	}

	public canViewDeveloperCurl(): boolean {
		return this.rolePermissions.hasPermission(Permission.VIEW_DEVELOPER_ACTION);
	}

	public processCurlPath(suffix = ''): string {
		return this.id ? `/process/${this.id}${suffix}` : '';
	}

	public operationCurlPath(operationUuid?: string | null): string {
		return operationUuid && this.id ? `/process/${this.id}/operations/${operationUuid}` : '';
	}

	/** `true` si la captura OCR procede de la app MiDNI (QR). */
	public isOcrMidniCapture(): boolean {
		const raw = this.detail?.data?.OCR?.capture_method;
		if (!raw || typeof raw !== 'string') {
			return false;
		}
		return raw.trim().toUpperCase() === OCR_CAPTURE_METHOD_MIDNI;
	}

	public getDetail(): void {
		this.error = null;
		this.resourceError = null;
		this.loading.show(this.trans.instant('LOADING.PROCESS_DETAIL'));

		if (this.processService.takePrefetchInitialNotFound(this.id)) {
			const notFoundError = new HttpErrorResponse({
				status: 404,
				error: { message: 'Process not found' },
			});
			this.error = notFoundError;
			this.resourceError = resolveDetailResourceError(notFoundError);
			this.detail = undefined;
			this.loading.hide();
			return;
		}

		this.processService.detailInitial(this.id).subscribe({
			next: (response: IProcessDetailResponse) => {
				this.applyInitialDetail(response);
			},
			error: (error: HttpErrorResponse) => {
				this.error = error;
				this.resourceError = resolveDetailResourceError(error);
				this.detail = undefined;
				this.loading.hide();
			},
			complete: () => {
				this.loading.hide();
			},
		});
	}

	private applyInitialDetail(response: IProcessDetailResponse): void {
		this.normalizeDetailResponse(response);
		this.detail = response;
		this.processHelper.setTimeline(this.detail);
		this.openFirstOperation();
		this.loadPostInitialSupplements();
		this.loadFieldHistory();
		this.runOcrPolLightChecks();
	}

	/** NIF-741 — carga histórico de campos editados en review. */
	private loadFieldHistory(): void {
		if (!this.id) {
			return;
		}
		this.processService
			.getHistory(this.id)
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				catchError((error: HttpErrorResponse) => {
					this.errorActionMsg =
						error?.error?.message ?? this.trans.instant('PROCESSES.DETAIL.FIELD_HISTORY_LOAD_ERROR');
					this.fieldHistoryByKey.set(new Map());
					return of(null);
				}),
			)
			.subscribe((response) => {
				if (!response) {
					return;
				}
				const map = new Map<string, IProcessFieldHistoryItem>();
				for (const item of response.fields ?? []) {
					// NIF-741: solo operaciones (no bloque identity/person).
					if (item.block === 'person') {
						continue;
					}
					map.set(`${item.block}|${item.field}`, item);
				}
				this.fieldHistoryByKey.set(map);
			});
	}

	/** Tras el initial: una sola petición de operaciones para galería + operación expandida. */
	private loadPostInitialSupplements(): void {
		globalThis.setTimeout(() => {
			this.loadStartupOperations();
			this.loadMediaSupplements();
		}, 150);
	}

	/** Una petición `view=operation` para miniaturas, operación expandida y tile Resultado. */
	private loadStartupOperations(): void {
		const previewKeys = this.getMissingPreviewImageKeys();
		const expandedKeys = this.getExpandedOperationLoadKeys();
		const resultKeys = this.getResultTileLoadKeys();
		const keys = [...new Set([...previewKeys, ...expandedKeys, ...resultKeys])];
		if (!keys.length) {
			return;
		}

		if (previewKeys.length) {
			this.previewImagesLoading.set(true);
		}
		const trackedOperationKeys = [...new Set([...expandedKeys, ...resultKeys])];
		if (trackedOperationKeys.length) {
			this.operationLoading.update((set) => {
				const next = new Set(set);
				trackedOperationKeys.forEach((key) => next.add(key));
				return next;
			});
		}

		this.processService
			.detailOperations(this.id, keys)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					// NIF-854: no silenciar 500 de hidratación OCR/POL (antes → solo images_debug).
					this.errorActionMsg = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => {
					this.previewImagesLoading.set(false);
					if (trackedOperationKeys.length) {
						this.operationLoading.update((set) => {
							const next = new Set(set);
							trackedOperationKeys.forEach((key) => next.delete(key));
							return next;
						});
					}
				}),
			)
			.subscribe((patch) => {
				if (patch?.data) {
					const loadedKeys = keys.filter((key) => !!patch.data?.[key]);
					if (loadedKeys.length) {
						this.applyOperationsTabPatch(patch, loadedKeys);
					}
				}
				// Evita loading infinito del tile Resultado si alguna key no vuelve (p. ej. choose INIT).
				this.markFullOperationKeysLoaded(keys);
			});
	}

	/** Keys que el tile Resultado exige en `loadedOperations` y aún no están listas. */
	private getResultTileLoadKeys(): OPERATION_TYPE[] {
		if (!this.shouldShowResultTile()) {
			return [];
		}
		return this.getResultTileOperationKeys().filter((key) => !this.isOperationContentReady(key));
	}

	private getResultTileOperationKeys(): OPERATION_TYPE[] {
		const pendingOps: OPERATION_TYPE[] = [];
		if (this.detail?.data?.OCR) pendingOps.push(OPERATION_TYPE.OCR);
		if (this.detail?.data?.POL) pendingOps.push(OPERATION_TYPE.POL);
		if (this.detail?.data?.FORM) pendingOps.push(OPERATION_TYPE.FORM);
		if (this.detail?.data?.DOCUMENTS) pendingOps.push(OPERATION_TYPE.DOCUMENTS);
		if (this.detail?.data?.EVALUATION) pendingOps.push(OPERATION_TYPE.EVALUATION);
		if (this.detail?.data?.ATTORNEY_POWERS) pendingOps.push(OPERATION_TYPE.ATTORNEY_POWERS);
		return pendingOps;
	}

	private getMissingPreviewImageKeys(): OPERATION_TYPE[] {
		const keys: OPERATION_TYPE[] = [];
		if (this.detail?.data?.OCR) {
			keys.push(OPERATION_TYPE.OCR);
		}
		if (this.detail?.data?.POL) {
			keys.push(OPERATION_TYPE.POL);
		}
		if (this.detail?.data?.DOCUMENTS) {
			keys.push(OPERATION_TYPE.DOCUMENTS);
		}
		return keys.filter((key) => !this.hasPreviewImagesForOperation(key));
	}

	private getExpandedOperationLoadKeys(): OPERATION_TYPE[] {
		if (!this.expandedOperation || this.isOperationContentReady(this.expandedOperation)) {
			return [];
		}
		return this.operationKeysIncludingLinked(this.expandedOperation);
	}

	private hasPreviewImagesForOperation(key: OPERATION_TYPE): boolean {
		const images = this.detail?.data?.[key]?.images;
		if (!images || typeof images !== 'object') {
			return false;
		}
		return Object.values(images).some(
			(urls) => Array.isArray(urls) && urls.some((url) => typeof url === 'string' && url.trim() !== ''),
		);
	}

	private loadMediaSupplements(): void {
		this.mediaSupplementsLoading.set(true);
		this.processService
			.detailMedia(this.id)
			.pipe(
				catchError(() => of(null)),
				finalize(() => this.mediaSupplementsLoading.set(false)),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
				this.normalizeDetailResponse(this.detail);
			});
	}

	private loadExpandedOperationContent(): void {
		if (!this.expandedOperation || this.isOperationContentReady(this.expandedOperation)) {
			return;
		}
		this.loadOperationsFullInParallel(this.operationKeysIncludingLinked(this.expandedOperation));
	}

	/** Full merge de `view=operation`: reutilizable por Operaciones, Comprobaciones y Actividad. */
	private applyOperationsTabPatch(patch: Partial<IProcessDetailResponse>, loadedKeys: OPERATION_TYPE[]): void {
		this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
		this.normalizeDetailResponse(this.detail);
		this.markFullOperationKeysLoaded(loadedKeys);
		// Recalcular edad tras hidratar OCR full (NIF-813); el initial lite no trae allowed_docs.
		this.runOcrPolLightChecks();
	}

	/** Marca operaciones con payload full de `view=operation` (no aplica a merges solo de imágenes). */
	private markFullOperationKeysLoaded(keys: OPERATION_TYPE[]): void {
		if (!keys.length) {
			return;
		}
		const mergeKeys = (set: Set<OPERATION_TYPE>) => {
			const next = new Set(set);
			keys.forEach((key) => next.add(key));
			return next;
		};
		this.loadedOperations.update(mergeKeys);
		this.checksOperationsHydrated.update(mergeKeys);
		this.activityOperationsHydrated.update(mergeKeys);
	}

	private operationKeysIncludingLinked(operation: OPERATION_TYPE): OPERATION_TYPE[] {
		const keys = [operation];
		if (operation === OPERATION_TYPE.POL) {
			keys.push(OPERATION_TYPE.OCR);
		} else if (operation === OPERATION_TYPE.OCR && this.detail?.data?.POL) {
			keys.push(OPERATION_TYPE.POL);
		}
		return keys;
	}

	private normalizeDetailResponse(response: IProcessDetailResponse): void {
		response.general.sms = Array.isArray(response?.general?.sms) ? response.general.sms : [];
		response.general.whatsapp = Array.isArray(response?.general?.whatsapp) ? response.general.whatsapp : [];
		if (response?.data?.POL?.data && typeof response.data.POL.data === 'string') {
			try {
				response.data.POL.data = JSON.parse(JSON.parse(response.data.POL.data));
			} catch (e) {
				console.error('Error al parsear el JSON:', e);
			}
		}
		if (response.general?.devices?.length) {
			response.general.devices.forEach((device) => applyProcessDeviceUserAgentParsing(device));
		}
		this.rewriteProcessImagesToLocal(response);

		const debugUrls = (response.images_debug ?? []).filter(
			(u): u is string => typeof u === 'string' && u.trim() !== '',
		);
		// Activa: todas las selfies en el visor (sin cambio). Estática: solo la oficial (NIF-822).
		const polSelfies =
			response.data?.POL?.mode === 'auto'
				? (response.data?.POL?.images?.selfie ?? [])
				: this.processHelper.polSelfiesForAdminPreview(
						response.data?.POL?.images?.selfie,
						response.data?.POL?.mode,
					);
		response.images = [
			...collectOcrAdminImageUrls(response.data?.OCR?.images),
			...polSelfies,
			...(response.data?.DOCUMENTS?.images?.document ?? []),
			...debugUrls,
		];
	}

	private runOcrPolLightChecks(): void {
		const ocr = this.detail?.data?.OCR;
		// Edad siempre que haya birthdate (OCR lite o identity). No gatear por allowed_docs (NIF-813).
		this.processHelper.setYearsOld(ocr, this.detail?.identity?.birthdate);
		if (ocr?.allowed_docs && ocr.antifraud) {
			this.processHelper.checkExpirationDate(ocr);
			this.processHelper.checkTypeDocument(ocr);
			this.processHelper.checkCountry(ocr);
		}
		if (this.detail?.data?.POL && this.processHelper.ocrDocumentAgeKnown) {
			this.processHelper.checkRangeAge(this.detail.data.POL);
		}
	}

	public tabSelected(tab: { id: string; label: string }): void {
		const tabId = tab.id as TAB_PROCESS;
		if (tabId === TAB_PROCESS.OPERATIONS || this.loadedSections().has(tabId)) {
			return;
		}

		switch (tabId) {
			case TAB_PROCESS.CHECKS:
				this.loadChecksTab();
				break;
			case TAB_PROCESS.ACTIVITY:
				this.loadActivityTab();
				break;
			case TAB_PROCESS.DETAILS:
				this.loadDetailsTab();
				break;
		}
	}

	private markTabLoaded(tab: TAB_PROCESS): void {
		this.loadedSections.update((set) => new Set(set).add(tab));
	}

	/** Operaciones del proceso que aún no tienen payload full en memoria (por contexto de pestaña). */
	private getUnloadedOperationKeys(keys: OPERATION_TYPE[], hydrated: Set<OPERATION_TYPE>): OPERATION_TYPE[] {
		return keys.filter((key) => this.detail?.data?.[key] && !hydrated.has(key));
	}

	/** HOME lite del initial trae `policyChecks: []`; Actividad necesita el payload full. */
	private isHomePolicyDataReady(): boolean {
		const home = this.detail?.data?.HOME;
		if (!home) {
			return true;
		}
		if (this.activityOperationsHydrated().has(OPERATION_TYPE.HOME)) {
			return true;
		}
		return (home.policyChecks?.length ?? 0) > 0;
	}

	private getActivityUnloadedOperationKeys(keys: OPERATION_TYPE[], hydrated: Set<OPERATION_TYPE>): OPERATION_TYPE[] {
		return keys.filter((key) => {
			if (!this.detail?.data?.[key]) {
				return false;
			}
			if (key === OPERATION_TYPE.HOME && !this.isHomePolicyDataReady()) {
				return true;
			}
			return !hydrated.has(key);
		});
	}

	private loadChecksTab(): void {
		const requiredKeys = this.getChecksRequiredOperationKeys();
		const missing = this.getUnloadedOperationKeys(requiredKeys, this.checksOperationsHydrated());
		if (!missing.length) {
			this.runOcrPolLightChecks();
			this.markTabLoaded(TAB_PROCESS.CHECKS);
			return;
		}

		this.tabLoading.set(TAB_PROCESS.CHECKS);
		this.processService
			.detailOperations(this.id, missing)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.errorActionMsg = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => this.tabLoading.set(null)),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.applyOperationPatch(patch, missing);
				this.runOcrPolLightChecks();
				this.markTabLoaded(TAB_PROCESS.CHECKS);
			});
	}

	/**
	 * La pestaña Actividad se construye en cliente (`setActivityLogs`); no usa `activity_log` del API.
	 * Solo hace falta cargar operaciones que falten en memoria.
	 */
	private loadActivityTab(): void {
		const requiredKeys = this.getActivityRequiredOperationKeys();
		const missing = this.getActivityUnloadedOperationKeys(requiredKeys, this.activityOperationsHydrated());
		if (!missing.length) {
			this.processHelper.setActivityLogs(this.detail);
			this.markTabLoaded(TAB_PROCESS.ACTIVITY);
			return;
		}

		this.tabLoading.set(TAB_PROCESS.ACTIVITY);
		this.processService
			.detailOperations(this.id, missing)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.errorActionMsg = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => this.tabLoading.set(null)),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.applyOperationPatch(patch, missing);
				this.processHelper.setActivityLogs(this.detail);
				this.markTabLoaded(TAB_PROCESS.ACTIVITY);
			});
	}

	private loadDetailsTab(): void {
		if (this.loadedSections().has(TAB_PROCESS.DETAILS)) {
			return;
		}

		this.tabLoading.set(TAB_PROCESS.DETAILS);
		this.processService
			.detailTab(this.id, 'details')
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.errorActionMsg = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => this.tabLoading.set(null)),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
				this.normalizeDetailResponse(this.detail);
				if (this.detail?.data?.HOME) {
					this.loadedOperations.update((set) => new Set(set).add(OPERATION_TYPE.HOME));
				}
				this.markTabLoaded(TAB_PROCESS.DETAILS);
			});
	}

	private getChecksRequiredOperationKeys(): OPERATION_TYPE[] {
		const keys: OPERATION_TYPE[] = [];
		if (this.detail?.data?.OCR) {
			keys.push(OPERATION_TYPE.OCR);
		}
		if (this.detail?.data?.POL) {
			keys.push(OPERATION_TYPE.POL);
		}
		if (this.detail?.data?.DOCUMENTS) {
			keys.push(OPERATION_TYPE.DOCUMENTS);
		}
		if (this.detail?.data?.EVALUATION) {
			keys.push(OPERATION_TYPE.EVALUATION);
		}
		if (this.detail?.data?.ATTORNEY_POWERS) {
			keys.push(OPERATION_TYPE.ATTORNEY_POWERS);
		}
		return keys;
	}

	/** Operaciones necesarias para `setActivityLogs` (no cargar FINISH ni conexiones genéricas). */
	private getActivityRequiredOperationKeys(): OPERATION_TYPE[] {
		const candidates: OPERATION_TYPE[] = [
			OPERATION_TYPE.HOME,
			OPERATION_TYPE.OCR,
			OPERATION_TYPE.POL,
			OPERATION_TYPE.DOCUMENTS,
			OPERATION_TYPE.EVALUATION,
			OPERATION_TYPE.ATTORNEY_POWERS,
			OPERATION_TYPE.TFA,
		];
		return candidates.filter((key) => !!this.detail?.data?.[key]);
	}

	private applyOperationPatch(patch: Partial<IProcessDetailResponse>, requestedKeys: OPERATION_TYPE[]): void {
		this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
		this.normalizeDetailResponse(this.detail);
		this.runOcrPolLightChecks();
		const loadedKeys = requestedKeys.filter((key) => !!patch.data?.[key]);
		this.markFullOperationKeysLoaded(loadedKeys);
	}

	public loadOperationsFullInParallel(keys: OPERATION_TYPE[]): void {
		const toQueue = keys.filter((key) => !this.loadedOperations().has(key));
		if (!toQueue.length) {
			return;
		}
		this.pendingOperationKeys.push(...toQueue);
		if (this.operationsFetchInFlight) {
			return;
		}
		this.flushPendingOperationsFetch();
	}

	private flushPendingOperationsFetch(): void {
		const toLoad = [...new Set(this.pendingOperationKeys.filter((k) => !this.loadedOperations().has(k)))];
		this.pendingOperationKeys = [];
		if (!toLoad.length) {
			this.operationsFetchInFlight = false;
			return;
		}

		this.operationsFetchInFlight = true;
		this.operationLoading.update((set) => {
			const next = new Set(set);
			toLoad.forEach((key) => next.add(key));
			return next;
		});

		this.processService
			.detailOperations(this.id, toLoad)
			.pipe(
				catchError((error: HttpErrorResponse) => {
					this.errorActionMsg = error?.error?.message ?? this.trans.instant('GENERAL.ERROR');
					return of(null);
				}),
				finalize(() => {
					this.operationLoading.update((set) => {
						const next = new Set(set);
						toLoad.forEach((key) => next.delete(key));
						return next;
					});
					this.operationsFetchInFlight = false;
					if (this.pendingOperationKeys.length) {
						this.flushPendingOperationsFetch();
					}
				}),
			)
			.subscribe((patch) => {
				if (!patch) {
					return;
				}
				this.detail = this.processHelper.mergeProcessDetail(this.detail, patch);
				this.normalizeDetailResponse(this.detail);
				this.runOcrPolLightChecks();
				this.markFullOperationKeysLoaded(toLoad);
			});
	}

	public isOperationLoading(operation: OPERATION_TYPE): boolean {
		return this.operationLoading().has(operation);
	}

	public isOperationContentReady(operation: OPERATION_TYPE): boolean {
		return this.loadedOperations().has(operation);
	}

	/** True mientras operaciones del bloque Resultados siguen cargándose (lazy fetch). */
	public isResultTileLoading(): boolean {
		if (!this.shouldShowResultTile()) {
			return false;
		}
		return this.getResultTileOperationKeys().some(
			(op) => this.isOperationLoading(op) || !this.isOperationContentReady(op),
		);
	}

	public shouldShowResultTile(): boolean {
		return !!(
			this.detail?.status_process === PROCESS_STATUS.KO ||
			this.detail?.status_process === PROCESS_STATUS.PENDING ||
			this.detail?.status_reasons ||
			this.processHelper.shouldShowOperationKoReason(this.detail?.data?.OCR) ||
			this.processHelper.shouldShowOperationKoReason(this.detail?.data?.POL) ||
			this.processHelper.resolveProcessMatchDataKoMessage(this.detail)
		);
	}

	public isMediaPreviewsLoading(): boolean {
		return this.previewImagesLoading() || this.mediaSupplementsLoading();
	}

	public isTabLoading(tab: TAB_PROCESS): boolean {
		return this.tabLoading() === tab;
	}

	public isTabContentReady(tab: TAB_PROCESS): boolean {
		return tab === TAB_PROCESS.OPERATIONS || this.loadedSections().has(tab);
	}

	public getDetailMenuOptions(): IMenu[] {
		const base = this.processHelper.detailMenuOpt(
			this.detail,
			this.detail?.status_process === PROCESS_STATUS.PENDING,
		);
		if (this.tabletSize && this.detail?.identity) {
			return [
				...base,
				{
					label: this.trans.instant('PROCESSES.DETAIL.IDENTITY'),
					icon: 'icon-user',
					id: MenuItemAction.SHOW_IDENTITY,
				},
			];
		}
		return base;
	}

	public detailMenuChange(action: IMenu, optionsSelected?: string[]): void {
		const actions = {
			[MenuItemAction.STATUS]: () => {
				this.showModalChangeStatus = true;
			},
			[MenuItemAction.BLOCK]: () => {
				this.selectedDeviceIp = null;
				this.showModalBlockIdentity = true;
				this.selectedOptionsIdentity = optionsSelected;
			},
			[MenuItemAction.UNLOCK]: () => {
				this.selectedDeviceIp = null;
				this.showModalUnlockIdentity = true;
				this.selectedOptionsIdentity = optionsSelected;
			},
			[MenuItemAction.AUDIT_SUMMARY]: () => {
				this.downloadAuditSummary();
			},
			[MenuItemAction.AUDIT_SIGNED]: () => {
				this.downloadAuditSigned();
			},
			[MenuItemAction.SHOW]: () => {
				const uuid = this.detail?.identity?.uuid;
				if (!uuid) return;
				this.router.navigate(['/identities/detail', uuid]);
			},
			[MenuItemAction.SHOW_IDENTITY]: () => {
				this.showModalIdentityDetail = true;
			},
			[MenuItemAction.SHOW_CURL]: () => {
				this.menuCurlBtn?.openFor('PROCESSES.CURL_MODAL.TITLE_DETAIL', this.processCurlPath());
			},
			[MenuItemAction.SHOW_LOGS]: () => {
				if (this.detail?.logs_url) {
					window.open(this.detail.logs_url, '_blank', 'noopener,noreferrer');
				}
			},
		};

		actions[action.id]?.() || null;
	}

	public downloadAuditSummary(): void {
		if (!this.id || this.downloadActive() !== 'none') {
			return;
		}
		this.downloadActive.set('summary');
		this.errorActionMsg = '';
		this.loading.show(this.trans.instant('LOADING.PROCESS_AUDIT_PDF'));
		this.processService
			.getAuditSummaryPdf(this.id)
			.pipe(
				finalize(() => {
					this.downloadActive.set('none');
					this.loading.hide();
				}),
			)
			.subscribe({
				next: (blob: Blob) => {
					this.saveBlobAsDownload(blob, `process_${this.id}_audit_summary.pdf`);
				},
				error: (error: HttpErrorResponse) => {
					void this.setPdfErrorMessage(error);
				},
			});
	}

	public downloadAuditSigned(): void {
		if (!this.id || this.downloadActive() !== 'none') {
			return;
		}
		this.downloadActive.set('signed');
		this.errorActionMsg = '';
		this.loading.show(this.trans.instant('LOADING.PROCESS_AUDIT_PDF'));
		this.processService
			.getAuditSummarySignedPdf(this.id)
			.pipe(
				finalize(() => {
					this.downloadActive.set('none');
					this.loading.hide();
				}),
			)
			.subscribe({
				next: (blob: Blob) => {
					this.saveBlobAsDownload(blob, `process_${this.id}_audit_summary_signed.pdf`);
				},
				error: (error: HttpErrorResponse) => {
					void this.setPdfErrorMessage(error);
				},
			});
	}

	private saveBlobAsDownload(blob: Blob, filename: string): void {
		const url = window.URL.createObjectURL(blob);
		const anchor = document.createElement('a');
		anchor.href = url;
		anchor.download = filename;
		document.body.appendChild(anchor);
		anchor.click();
		document.body.removeChild(anchor);
		window.URL.revokeObjectURL(url);
	}

	private async setPdfErrorMessage(error: HttpErrorResponse): Promise<void> {
		let message = this.trans.instant('PROCESSES.DETAIL.AUDIT_DOWNLOAD_ERROR');
		try {
			if (error.error instanceof Blob) {
				const text = await error.error.text();
				const parsed = JSON.parse(text) as { message?: string };
				if (parsed?.message) {
					message = parsed.message;
				}
			} else if (error.error && typeof error.error === 'object' && 'message' in error.error) {
				message = String((error.error as { message: string }).message);
			}
		} catch {
			message = this.trans.instant('PROCESSES.DETAIL.AUDIT_DOWNLOAD_ERROR_UNREADABLE');
		}
		this.errorActionMsg = message;
	}

	public dataJsonMenuChange(action: IMenu): void {
		const actions = {
			[MenuItemAction.SHOW]: () => (this.showModalJsonViewer = true),
			[MenuItemAction.COPY]: () => this.copyProcessJsonToClipboard(),
			[MenuItemAction.DOWNLOAD]: () => {
				downloadJsonAsTxt((this.fullName ? this.fullName + ' - ' : '') + this.detail.process_uuid, this.detail);
				this.trackProcessJsonDownload();
			},
		};

		actions[action.id]() || null;
	}

	private copyProcessJsonToClipboard(): void {
		if (!this.detail) {
			return;
		}
		const text = JSON.stringify(this.detail, null, 2);
		void navigator.clipboard.writeText(text).then(
			() => undefined,
			() => this.fallbackCopyToClipboard(text),
		);
	}

	private fallbackCopyToClipboard(text: string): void {
		const ta = document.createElement('textarea');
		ta.value = text;
		ta.setAttribute('readonly', '');
		ta.style.position = 'absolute';
		ta.style.left = '-9999px';
		document.body.appendChild(ta);
		ta.select();
		try {
			document.execCommand('copy');
		} finally {
			document.body.removeChild(ta);
		}
	}

	public closeChangeStatus(reload: boolean): void {
		if (reload) {
			this.getDetail();
		}
		this.showModalChangeStatus = false;
	}

	public showViewMedia(imgSelected: string): void {
		if (!imgSelected || !this.detail?.images?.length) {
			return;
		}
		const reference = this.getProcessActivityReference();
		this.viewMedia.setViewMedia({
			show: true,
			title: this.fullName,
			tag: {
				label: this.trans.instant('MASTER.STATUS.' + this.detail.status_process),
				status: this.masterService.getTagStatus(this.detail.status_process),
			},
			subtitle: this.masterService.getDocumentTypeLabel(this.detail?.data?.OCR?.document_type),
			activity: {
				type: 'processes',
				text: `Descarga de imágenes capturadas del proceso ${reference}`,
				link: this.userActivityTracker.buildLink(`/processes/detail/${this.id}`, reference),
				companyUuid: this.detail?.general?.company_uuid,
			},
			downloadFileName: `process_${this.id}_image.jpg`,
			developerCurl: this.canViewDeveloperCurl()
				? {
						path: this.processCurlPath('/media'),
						titleKey: 'PROCESSES.CURL_MODAL.TITLE_MEDIA',
					}
				: null,
			data: this.detail.images.map((image: string) => ({
				type: 'image' as const,
				doc: image,
				selected: image === imgSelected,
				scale: 1.0,
			})),
		});
	}

	get fullName(): string {
		if (this.detail && this.detail?.identity?.names && this.detail?.identity?.surnames) {
			return (this.detail?.identity.names ?? '') + ' ' + (this.detail?.identity.surnames ?? '');
		}
		return notAvailableLabel(this.trans);
	}

	/** Etiqueta de origen del link auth (null/omitido → GENERAL.NOT_AVAILABLE). */
	public authOriginLabel(): string {
		const origin = this.detail?.general?.auth_origin;
		if (origin === 'admin' || origin === 'api') {
			return this.trans.instant(`PROCESSES.DETAIL.AUTH_ORIGIN_VALUE.${origin}`);
		}
		return notAvailableLabel(this.trans);
	}

	public getOperationTagIcon(state: OPERATION_STATUS): { icon: Icons; status: ITagIconStatus } {
		if (state === OPERATION_STATUS.KO) {
			return { icon: 'icon-alert', status: 'error' };
		}
		if (state === OPERATION_STATUS.OK) {
			return { icon: 'icon-check', status: 'success' };
		}
		return { icon: 'icon-minus', status: 'default' };
	}

	public getTagIconItem(status: BOOLEAN_STATUS | string): ITagIcon {
		return status === 'OK'
			? { icon: 'icon-check', status: 'success', size: 'sm' }
			: { icon: 'icon-alert', status: 'error', size: 'sm' };
	}

	/**
	 * Obtiene las claves (UUIDs) de un objeto de documentos.
	 *
	 * @param docRecord - El objeto que contiene documentos con UUIDs como claves.
	 * @returns Un array con las claves del objeto.
	 */
	public getDocumentKeys(docRecord: Record<string, any>): string[] {
		return Object.keys(docRecord || {});
	}

	public closeBlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalBlockIdentity = false;
		if (payload && typeof payload === 'object' && this.id) {
			this.blockIdentity(payload, false);
		} else {
			this.selectedDeviceIp = null;
		}
	}

	public closeUnlockIdentity(payload: boolean | Partial<IIdentityBlockPayload>): void {
		this.showModalUnlockIdentity = false;
		if (payload && typeof payload === 'object' && this.id) {
			this.blockIdentity(payload, true);
		} else {
			this.selectedDeviceIp = null;
		}
	}

	public openUserAgentModal(device: DeviceUserAgentInput): void {
		this.deviceForUserAgentModal = device;
	}

	public closeUserAgentModal(): void {
		this.deviceForUserAgentModal = null;
	}

	/**
	 * Candado IP de la tile Dispositivos (NIF-353): una IP concreta del dispositivo.
	 * - Esa IP en `locks.blocked_ips` → unlock + Desbloquear
	 * - Esa IP libre → lock + Bloquear
	 */
	public onDevicesBlockUnlock(event: ProcessDeviceBlockUnlockEvent): void {
		if (!event?.action || !event.device) {
			return;
		}
		this.selectedDeviceIp = event.device.ip?.trim() || null;
		this.selectedOptionsIdentity = ['ip'];
		if (event.action === 'block') {
			this.showModalBlockIdentity = true;
		} else {
			this.showModalUnlockIdentity = true;
		}
	}

	private blockIdentity(payload: Partial<IIdentityBlockPayload>, isUnlock: boolean): void {
		const loadingKey = isUnlock ? 'LOADING.UNLOCKING_IDENTITY' : 'LOADING.BLOCKING_IDENTITY';
		this.loading.show(this.trans.instant(loadingKey));

		this.identitiesService.blockIdentity(this.getBlockIdentityBody(payload)).subscribe({
			next: () => {
				this.loading.hide();
				this.selectedDeviceIp = null;
				this.getDetail();
			},
			error: (error: HttpErrorResponse) => {
				this.loading.hide();
				this.errorActionMsg = error?.error?.message || this.trans.instant('ERRORS.GENERIC_DESCRIPTION');
			},
		});
	}

	private getBlockIdentityBody(payload: Partial<IIdentityBlockPayload>): IIdentityBlockPayload {
		if (payload.blocked_ip === true || payload.blocked_ip === false) {
			if (this.selectedDeviceIp) {
				payload.ip = [this.selectedDeviceIp];
			} else if (payload.blocked_ip === true) {
				payload.ip = (this.detail.general.devices ?? [])
					.map((device) => device.ip)
					.filter((ip): ip is string => !!ip?.trim());
			} else {
				payload.ip = (this.detail.locks?.blocked_ips ?? [])
					.map((entry) => entry.ip)
					.filter((ip): ip is string => !!ip?.trim());
			}
		}

		if (payload.blocked_identifier === true || payload.blocked_identifier === false) {
			if (this.detail.identity.identifier) payload.identifier = this.detail.identity.identifier;
		}

		if (payload.blocked_face === true) {
			if (this.detail?.data?.POL && this.detail?.data?.POL?.data) {
				payload.operation_uuid = this.detail.data.POL.uuid;
			}
		}

		return {
			...payload,
			process_uuid: this.id,
			company_uuid: this.detail.general.company_uuid,
		};
	}

	public showActivityModal(action: MenuItemAction, info: PolicyModalInfo | { title: string; data: unknown }): void {
		if (action === MenuItemAction.SHOW) {
			this.policyModalInfo = normalizePolicyModalInfo(info as PolicyModalInfo, this.languageService);
		}
		if (action === MenuItemAction.SHOW_JSON) {
			this.activityJsonModal = info as { title: string; data: unknown };
		}
	}

	public policyModalName(info: PolicyModalInfo): string {
		if (info.isExtra && !info.origin) {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_AUTH_EXTRA');
		}
		if (info.custom_id === 'tecalis_default_check_1') {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_TECALIS');
		}
		if (info.isExtra) {
			return this.trans.instant('PROCESSES.DETAIL.POLICY_AUTH_EXTRA');
		}
		return info.custom_id ?? '';
	}

	public policyModalOrigin(info: PolicyModalInfo): string {
		return policyOriginLabel(resolvePolicyOrigin(info), this.trans);
	}

	public getInfoPhoto(type: 'front' | 'back'): string {
		// NIF-985: ocrConf en data.client_capture; fallback details (OK históricos).
		const ocrDetails = this.detail.data?.OCR?.details;
		const legacyOcrConf =
			ocrDetails && typeof ocrDetails === 'object' && 'ocrConf' in ocrDetails
				? ocrDetails.ocrConf
				: undefined;
		const ocrConf = this.detail.data?.OCR?.data?.client_capture?.ocrConf ?? legacyOcrConf;
		const photos = ocrConf?.document?.find((doc) => doc.documentTestId === type)?.photos;
		if (!photos?.length) return '';
		let result = '';
		const bestPhoto = photos.find((p: { selected?: boolean; lapacian?: number; fourier?: number }) => p.selected);
		if (bestPhoto?.lapacian) {
			result += `<span>Lapacian: ${bestPhoto.lapacian.toFixed(2)}</span>`;
		}
		if (bestPhoto?.fourier) {
			result += `<span>Fourier: ${bestPhoto.fourier.toFixed(2)}</span>`;
		}

		return result;
	}

	get tagRoundedByProcess(): ITagStats | null {
		if (this.detail?.general?.average_workFlow && this.detail?.general?.total_time != null) {
			const average =
				((this.detail.general.total_time - this.detail.general.average_workFlow) /
					this.detail.general.average_workFlow) *
				100;
			if (average < 0) {
				return { icon: 'icon-trending-up', label: Math.abs(average).toFixed(0) + '%' };
			} else if (average > 0) {
				return { icon: 'icon-trending-down', label: Math.abs(average).toFixed(0) + '%', styling: 'red' };
			}
		}
		return null;
	}

	private openFirstOperation(): void {
		if (!this.detail?.data) return;

		// Obtener la primera operación que no sea HOME ni FINISH
		const firstOperation = Object.keys(this.detail.data).find(
			(key) =>
				key !== 'HOME' &&
				key !== 'FINISH' &&
				this.detail.data[key] &&
				(this.detail.data[key].state === OPERATION_STATUS.OK ||
					this.detail.data[key].state === OPERATION_STATUS.KO),
		) as OPERATION_TYPE | undefined;

		// Si se encuentra una operación, expandirla
		if (firstOperation) this.expandedOperation = firstOperation;
	}

	public toggleCollapse(operationType: OPERATION_TYPE, expanded?: boolean): void {
		this.expandedOperation = !expanded ? null : operationType;
		if (!expanded || this.isOperationContentReady(operationType)) {
			return;
		}
		this.loadOperationsFullInParallel(this.operationKeysIncludingLinked(operationType));
	}

	public goBack({ event }: IBackEvent): void {
		this.navigationService.navigateTo({ routeSegments: ['processes'] }, event);
	}

	private rewriteProcessImagesToLocal(response: IProcessDetailResponse): void {
		// Sin flags extra: si `pwcs` es localhost, se asume el mismo escenario que APP_ENV=local en ese backend.
		if (!this.isPwcsLocalDev() || !response?.data) {
			return;
		}

		const mapImages = (images?: string[]) => images?.map((img) => this.toLocalStorageUrl(img));
		const mapUrl = (url?: string | null) => (url ? this.toLocalStorageUrl(url) : url);
		if (response.data.OCR?.images) {
			response.data.OCR.images.ocr_front = mapImages(response.data.OCR.images.ocr_front);
			response.data.OCR.images.ocr_back = mapImages(response.data.OCR.images.ocr_back);
			response.data.OCR.images.ocr_front_clear = mapImages(response.data.OCR.images.ocr_front_clear);
			response.data.OCR.images.ocr_back_clear = mapImages(response.data.OCR.images.ocr_back_clear);
			response.data.OCR.images.temporary = mapImages(response.data.OCR.images.temporary);
			if (response.data.OCR.images.ocr_preview) {
				response.data.OCR.images.ocr_preview = {
					front: mapUrl(response.data.OCR.images.ocr_preview.front) ?? null,
					back: mapUrl(response.data.OCR.images.ocr_preview.back) ?? null,
				};
			}
		}
		if (response.data.POL?.images) {
			response.data.POL.images.selfie = mapImages(response.data.POL.images.selfie);
			response.data.POL.images.temporary = mapImages(response.data.POL.images.temporary);
		}
		if (response.data.DOCUMENTS?.images) {
			response.data.DOCUMENTS.images.document = mapImages(response.data.DOCUMENTS.images.document);
		}
		if (response.images_debug?.length) {
			response.images_debug = mapImages(response.images_debug);
		}
	}

	private toLocalStorageUrl(imageUrl: string): string {
		if (!imageUrl || imageUrl.includes('/process/image?path=')) {
			return imageUrl;
		}

		try {
			const parsed = new URL(imageUrl);
			const looksLikeGoogle =
				parsed.hostname.includes('storage.googleapis.com') || parsed.searchParams.has('X-Goog-Algorithm');

			if (!looksLikeGoogle) {
				return imageUrl;
			}

			// Path-style: storage.googleapis.com/BUCKET/objeto/...
			// Virtual-hosted: BUCKET.storage.googleapis.com/objeto/... (no quitar el primer segmento del path)
			const objectPath = this.extractGcsObjectPath(parsed);
			if (!objectPath) {
				return imageUrl;
			}

			return `${environment.pwcs}/process/image?path=${encodeURIComponent(objectPath)}`;
		} catch {
			return imageUrl;
		}
	}

	/** Ruta del objeto dentro del bucket (sin nombre de bucket). */
	private extractGcsObjectPath(parsed: URL): string | null {
		const host = parsed.hostname.toLowerCase();
		const pathname = decodeURIComponent(parsed.pathname).replace(/^\/+/, '');

		if (host.endsWith('.storage.googleapis.com')) {
			return pathname || null;
		}

		if (host === 'storage.googleapis.com' || host === 'www.storage.googleapis.com') {
			const slash = pathname.indexOf('/');
			if (slash === -1) {
				return null;
			}
			return pathname.slice(slash + 1) || null;
		}

		if (host === 'storage.cloud.google.com') {
			const slash = pathname.indexOf('/');
			if (slash === -1) {
				return null;
			}
			return pathname.slice(slash + 1) || null;
		}

		return null;
	}

	/** PWCS en host local (p. ej. http://localhost:8086/api) → reescribir imágenes hacia /process/image. */
	private isPwcsLocalDev(): boolean {
		try {
			const u = new URL(environment.pwcs);
			return u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '0.0.0.0';
		} catch {
			return false;
		}
	}

	private trackProcessJsonDownload(): void {
		const reference = this.getProcessActivityReference();
		this.userActivityTracker.track({
			type: 'processes',
			text: `Descarga del json del proceso ${reference}`,
			link: this.userActivityTracker.buildLink(`/processes/detail/${this.id}`, reference),
			company_uuid: this.detail?.general?.company_uuid,
		});
	}

	private getProcessActivityReference(): string {
		return this.userActivityTracker.shortReference(this.detail?.process_uuid || this.id, 4, 2);
	}

	public messagesCount(): number {
		return mergeSentMessages(this.detail?.general?.sms, this.detail?.general?.whatsapp).length;
	}

	public openSentMessagesModal(): void {
		if (this.messagesCount() <= 0) {
			return;
		}
		this.showSentMessagesModal.set(true);
	}

	public closeSentMessagesModal(): void {
		this.showSentMessagesModal.set(false);
	}
}
